import { useEffect, useMemo, useState } from "react";
import axios from 'axios';
import { getAuth, onIdTokenChanged } from "firebase/auth";

const useAxios = () => {
  const auth = getAuth();
  const [idToken, setIdToken] = useState(null);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken(); // not forced
        setIdToken(token);
      } else {
        setIdToken(null);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const idToken = localStorage.getItem("idToken");
    if (idToken) {
      setIdToken(idToken);
    }
  }, []);

  
  // Memoize axios instance to prevent re-creation on every render
  const memorizedAxiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
    });

    instance.interceptors.request.use(
      (config) => {
        if (idToken) {
          config.headers.Authorization = `Bearer ${idToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const user = auth.currentUser;
            if (user) {
              const newToken = await user.getIdToken(true); // Force refresh token
              setIdToken(newToken);
              localStorage.setItem("idToken", newToken);

              const newInstance = axios.create({ baseURL: process.env.REACT_APP_API_URL });

              originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
              return newInstance(originalRequest);
            }
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [idToken, auth.currentUser]);


  return { axiosInstance: memorizedAxiosInstance, idToken };
};

export default useAxios;
